import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import api from '../../utils/api';
import { money, moneyK, fecha, mesLabel, Kpi, Title, Badge, Empty, ESTADO_COBRO, ESTADO_RENOVACION } from './ui';

const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };

export default function AgenciaPanel() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get('/agency/resumen').then(r => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const serie = (d.serie || []).map(s => ({ mes: mesLabel(s.mes), cobrado: Number(s.cobrado), pendiente: Number(s.pendiente), a_pasar: Number(s.a_pasar || 0), cant: Number(s.cant_cobrados), pend: Number(s.cant_pendientes) }));
  const card = { marginBottom: 20 };
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };

  return (
    <div>
      <Title>Panel de la agencia</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Cobrado este mes" value={moneyK(d.mes.total)} sub={`${d.mes.cant} cobros`} />
        <Kpi label="Cobrado en el año" value={moneyK(d.anio.total)} />
        <Kpi label="Abonos mensuales" value={moneyK(d.mrr.total)} sub={`${d.mrr.cant} servicios activos`} color="var(--accent)" />
        <Kpi label="Por cobrar" value={moneyK(d.pendiente.total)} sub={`${d.pendiente.cant} cobros`} color="#f59e0b" onClick={() => navigate('/agencia/cobros')} />
        <Kpi label="Vencido" value={moneyK(d.vencido.total)} sub={`${d.vencido.cant} cobros`} color="#ef4444" onClick={() => navigate('/agencia/cobros')} />
        <Kpi label="A pasar" value={moneyK((d.a_pasar || {}).total)} sub={`${(d.a_pasar || {}).cant || 0} cuotas futuras`} color="#64748b" onClick={() => navigate('/agencia/cobros')} />
        <Kpi label="Clientes activos" value={d.clientes_activos} onClick={() => navigate('/agencia/clientes')} />
      </div>

      <div className="card" style={card}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cobrado y pendiente por mes</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Últimos 12 meses. Los pendientes se cuentan en el mes en que había que cobrarlos.</div>
        {serie.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={serie} margin={{ left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={{ fill: 'var(--chart-text)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--chart-text)', fontSize: 11 }} tickFormatter={moneyK} width={70} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v), n]} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--chart-text)' }} />
              <Bar dataKey="cobrado" name="Cobrado" stackId="a" fill="var(--accent)" radius={[0, 0, 0, 0]} />
              <Bar dataKey="pendiente" name="Pendiente" stackId="a" fill="#f59e0b" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty>Sin datos</Empty>}
      </div>

      {serie.some(s => s.pendiente > 0) && (
        <div className="card" style={card}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Pendientes por mes</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Cobros de meses anteriores que todavía no entraron</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {serie.filter(s => s.pendiente > 0).map(s => (
              <div key={s.mes} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', minWidth: 120 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{s.mes}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#f59e0b' }}>{moneyK(s.pendiente)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.pend} cobros</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Por cobrar</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Lo que falta cobrar, por vencimiento</div>
          {d.por_cobrar.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Cliente</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}></th></tr></thead>
              <tbody>{d.por_cobrar.map(c => {
                const e = ESTADO_COBRO[c.estado] || {};
                return (
                  <tr key={c.id}>
                    <td style={td}><div style={{ fontWeight: 600 }}>{c.cliente}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.concepto || '—'}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(c.vencimiento)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                    <td style={td}><Badge label={e.label} color={e.color} bg={e.bg} /></td>
                  </tr>
                );
              })}</tbody>
            </table>
          ) : <Empty>No hay cobros pendientes 🎉</Empty>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Renovaciones de este mes</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Hosting que vence el último día del mes</div>
          {d.renovaciones.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Cliente</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th></tr></thead>
              <tbody>{d.renovaciones.map(r => {
                const e = ESTADO_RENOVACION[r.estado_renovacion] || {};
                return (
                  <tr key={r.id} onClick={() => navigate('/agencia/renovaciones')} style={{ cursor: 'pointer' }}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.cliente}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(r.proximo_vencimiento)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(r.monto)}</td>
                    <td style={td}><Badge label={e.label} color={e.color} /></td>
                  </tr>
                );
              })}</tbody>
            </table>
          ) : <Empty>Este mes no vence ningún hosting</Empty>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Últimos pagos</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Lo último que entró</div>
          {d.ultimos_pagos.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>{d.ultimos_pagos.map(p => (
                <tr key={p.id}>
                  <td style={td}><div style={{ fontWeight: 600 }}>{p.cliente}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.concepto || '—'}</div></td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{fecha(p.fecha)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>{money(p.monto)}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <Empty>Sin pagos registrados</Empty>}
        </div>
      </div>
    </div>
  );
}
